import React                    from 'react';
import { observer }             from 'mobx-react-lite';
import ColorPicker              from '@widesk-ui/components/form/ColorPicker';
import Tooltip                  from '@widesk-ui/components/Tooltip';
import View                     from '@widesk-ui/components/View';
import { BgColorsOutlined }     from '@ant-design/icons';
import { LeftOutlined }         from '@ant-design/icons';
import { RightOutlined }        from '@ant-design/icons';
import { ThemeSlider }          from '@/TokamapEditor/ux/ThemeFields';
import Field                    from '@/TokamapEditor/ux/AreaSidebar/Field';
import usedColorsStore          from '@/TokamapEditor/stores/usedColorsStore';
import useDesignTokens          from '@/hooks/useDesignTokens';
import { DesignTokens }         from '@/hooks/useDesignTokens';
import leftSidebarStore         from '@/TokamapEditor/stores/leftSidebarStore';
import type { ThemeLockRef }    from '@/TokamapEditor/helpers/themeImposed';

const MIXED_PATTERN = 'repeating-linear-gradient(45deg, #ff000033 0 4px, #0000ff33 4px 8px)';
const PALETTE = [
	'#e5534b', '#f08c2e', '#f2c94c', '#4caf6e', '#3b82c4', '#8e6cc0', '#ffffff',
	'#a32d26', '#b35f12', '#a67c00', '#2e7044', '#1f4f82', '#5b3f8c', '#000000',
];
const COLUMNS = 7;
const USED_ROWS = 2;
const MODE_STORAGE_KEY = 'tokamap.colorPicker.customizing';
const HEX_WITH_ALPHA = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i;

function splitOpacity(color?: string): { base?: string; opacity: number } {
	const match = color?.match(HEX_WITH_ALPHA);
	if (!match) return { base: color, opacity: 1 };

	return { base: `#${match[1]}`, opacity: match[2] ? parseInt(match[2], 16) / 255 : 1 };
}

function withOpacity(color: string, opacity: number): string {
	const { base } = splitOpacity(color);
	if (opacity >= 1 || !base || !HEX_WITH_ALPHA.test(base)) return base ?? color;

	return base + Math.round(opacity * 255).toString(16).padStart(2, '0');
}

function readCustomizing(): boolean {
	try {
		return localStorage.getItem(MODE_STORAGE_KEY) === '1';
	} catch {
		return false;
	}
}

function writeCustomizing(customizing: boolean) {
	try {
		localStorage.setItem(MODE_STORAGE_KEY, customizing ? '1' : '0');
	} catch {
		// Stockage indisponible (navigation privée) : le mode reste celui de la session.
	}
}

const checkerboard = (tokens: DesignTokens) =>
	`repeating-conic-gradient(${tokens.border.default} 0 25%, transparent 0 50%) 0 0 / 8px 8px`;

type PaletteProps = {
	value?: string;
	mixed?: boolean;
	onChange: (color?: string) => void;
	allowClear?: boolean;
	clearLabel?: string;
	/** Faux : la couleur choisie ne rejoint pas les couleurs du projet. */
	remember?: boolean;
	alpha?: boolean;
};

function pickColor(color: string | undefined, current: string | undefined, { alpha, remember = true, onChange }: Pick<PaletteProps, 'alpha' | 'remember' | 'onChange'>) {
	const chosen = color && alpha ? withOpacity(color, splitOpacity(current).opacity) : color;
	onChange(chosen);
	if (chosen && remember) usedColorsStore.remember(chosen);
}

const linkStyle = (tokens: DesignTokens): React.CSSProperties => ({
	display: 'flex',
	alignItems: 'center',
	gap: 6,
	padding: 0,
	border: 'none',
	background: 'none',
	cursor: 'pointer',
	fontSize: 13,
	color: tokens.color.primary,
});

/** Pastilles, couleurs du projet et opacité, sans bouton autour : `picker` remplace les pastilles. */
export const ColorPalette = observer(({ value, mixed, onChange, allowClear = true, clearLabel = 'Aucune couleur', remember = true, alpha = false, picker, action }: PaletteProps & {
	picker?: React.ReactNode;
	action?: React.ReactNode;
}) => {
	const tokens = useDesignTokens();

	const { base, opacity } = splitOpacity(value);
	const commit = (color?: string) => {
		onChange(color);
		if (color && remember) usedColorsStore.remember(color);
	};
	const choose = (color?: string) => pickColor(color, value, { alpha, remember, onChange });

	const used = [...new Set(usedColorsStore.colors(COLUMNS * USED_ROWS).map(color => (alpha ? color : splitOpacity(color).base!)))];

	const swatchButton = (color: string, exact = false) => {
		const selected = !mixed && (exact ? value?.toLowerCase() : base?.toLowerCase()) === color;
		const title = color.toUpperCase();

		return (
			<button
				key={color}
				type="button"
				aria-label={title}
				aria-pressed={selected}
				title={title}
				onClick={() => (exact ? commit(color) : choose(color))}
				style={{
					width: '100%',
					aspectRatio: '1',
					padding: 0,
					borderRadius: '50%',
					cursor: 'pointer',
					border: `1px solid ${tokens.border.default}`,
					outline: selected ? `2px solid ${tokens.color.primary}` : 'none',
					outlineOffset: 2,
					background: `linear-gradient(${color}, ${color}), ${checkerboard(tokens)}`,
				}}
			/>
		);
	};

	const swatchGrid = (colors: string[], exact = false) => (
		<div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLUMNS}, 1fr)`, gap: 6 }}>
			{colors.map(color => swatchButton(color, exact))}
		</div>
	);

	return (
		<View gap="sm">
			{picker ?? (
				<>
					{swatchGrid(PALETTE)}

					{!!used.length && (
						<View gap={6}>
							<View size={12} color={tokens.text.muted}>Utilisées dans le projet</View>
							{swatchGrid(used, true)}
						</View>
					)}
				</>
			)}

			{alpha && !!value && (
				<Field label="Opacité">
					<ThemeSlider
						value={Math.round(opacity * 100)}
						min={0}
						max={100}
						step={1}
						suffix="%"
						style={{ margin: '0 0 4px' }}
						onSave={async percent => commit(withOpacity(value, percent / 100))}
					/>
				</Field>
			)}

			<View row spread centerV>
				{allowClear && !picker ? (
					<button type="button" aria-pressed={!value} onClick={() => commit(undefined)} style={{ ...linkStyle(tokens), color: tokens.text.secondary }}>
						{clearLabel}
					</button>
				) : <span />}

				{action}
			</View>
		</View>
	);
});

/** La palette posée telle quelle dans un panneau ; seul « Personnaliser » ouvre le sélecteur libre. */
export const InlineColorPalette = observer((props: PaletteProps) => {
	const tokens = useDesignTokens();
	const { base } = splitOpacity(props.value);

	return (
		<ColorPalette
			{...props}
			action={(
				<ColorPicker
					placement="rightTop"
					value={base}
					showText={false}
					onChangeComplete={color => pickColor(color, props.value, props)}
					panelRender={(_panel, { components: { Picker } }) => <View style={{ width: 236 }}><Picker /></View>}
				>
					<button type="button" style={linkStyle(tokens)}>
						Personnaliser<RightOutlined style={{ fontSize: 10 }} />
					</button>
				</ColorPicker>
			)}
		/>
	);
});

/** Bouton compact « pastille + libellé » qui ouvre le sélecteur de couleur. */
const ColorButton = observer(({ label, value, mixed, onChange, allowClear = true, clearLabel = 'Aucune couleur', remember = true, alpha = false, lockedBy, block }: PaletteProps & {
	label: string;
	lockedBy?: ThemeLockRef;
	block?: boolean;
}) => {
	const tokens = useDesignTokens();
	const [customizing, setCustomizing] = React.useState(readCustomizing);

	const { base } = splitOpacity(value);

	const swatch = value
		? `linear-gradient(${value}, ${value}), ${checkerboard(tokens)}`
		: (mixed ? MIXED_PATTERN : checkerboard(tokens));

	const button = (
		<button
			type="button"
			aria-label={`Couleur : ${label}`}
			aria-disabled={!!lockedBy}
			title={lockedBy ? undefined : (mixed ? 'Plusieurs valeurs' : value?.toUpperCase())}
			onClick={lockedBy ? () => leftSidebarStore.openThemeField(lockedBy.target) : undefined}
			style={{
				width: block ? '100%' : undefined,
				minWidth: 0,
				height: block ? 52 : 40,
				padding: block ? '0 12px' : '0 10px',
				borderRadius: 8,
				cursor: lockedBy && !lockedBy.target ? 'not-allowed' : 'pointer',
				opacity: lockedBy ? 0.5 : 1,
				display: 'inline-flex',
				alignItems: 'center',
				gap: block ? 10 : 8,
				textAlign: 'left',
				fontSize: 14,
				color: tokens.text.default,
				border: `1px solid ${mixed ? tokens.color.primary : tokens.border.default}`,
				borderStyle: mixed ? 'dashed' : 'solid',
				background: tokens.surface.elevated,
			}}
		>
			<span
				style={{
					width: block ? 24 : 18,
					height: block ? 24 : 18,
					borderRadius: '50%',
					flexShrink: 0,
					border: `1px solid ${tokens.border.default}`,
					background: swatch,
				}}
			/>
			{block ? (
				<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
					<span>{label}</span>
					<span style={{ fontSize: 12, color: tokens.text.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{mixed ? 'Plusieurs valeurs' : (value?.toUpperCase() ?? clearLabel)}
					</span>
				</span>
			) : label}
			{!!lockedBy && <BgColorsOutlined style={{ color: tokens.color.primary, fontSize: 12 }} />}
		</button>
	);

	if (lockedBy) {
		return <Tooltip title={lockedBy.target ? `${label} : imposé par le thème « ${lockedBy.theme} », cliquer pour le régler dans le thème` : `${label} : imposé par ${lockedBy.theme}`}>{button}</Tooltip>;
	}

	const toggleMode = () => {
		writeCustomizing(!customizing);
		setCustomizing(!customizing);
	};

	return (
		<ColorPicker
			placement="rightTop"
			value={base}
			showText={false}
			onChangeComplete={color => pickColor(color, value, { alpha, remember, onChange })}
			onOpenChange={open => open && setCustomizing(readCustomizing())}
			panelRender={(_panel, { components: { Picker } }) => (
				<View style={{ width: 236 }}>
					<ColorPalette
						value={value}
						mixed={mixed}
						onChange={onChange}
						allowClear={allowClear}
						clearLabel={clearLabel}
						remember={remember}
						alpha={alpha}
						picker={customizing ? <Picker /> : undefined}
						action={(
							<button type="button" onClick={toggleMode} style={linkStyle(tokens)}>
								{customizing ? <><LeftOutlined style={{ fontSize: 10 }} />Palette</> : <>Personnaliser<RightOutlined style={{ fontSize: 10 }} /></>}
							</button>
						)}
					/>
				</View>
			)}
		>
			{button}
		</ColorPicker>
	);
});

export default ColorButton;
